import { Transform } from 'class-transformer';
import { IsIn, IsNotEmpty, IsObject, IsString, MaxLength, ValidateIf } from 'class-validator';
import {
  SURVEY_INVITE_NODE_ID_MAX_LENGTH,
  SURVEY_RESPONDENT_NAME_MAX_LENGTH,
  SURVEY_RESPONDENT_ROLE_OTHER_MAX_LENGTH,
  SURVEY_RESPONDENT_ROLES,
  type SurveyInviteItemStatus,
  type SurveyInvitePdfStatus,
  type SurveyRespondentRole,
} from '../../types/survey-invite';
import type { SurveyAnswers } from '../survey/survey-answers.types';
import type { SurveySchema } from '../survey/survey-schema.types';

function trimString({ value }: { value: unknown }): unknown {
  return typeof value === 'string' ? value.trim() : value;
}

export interface PublicSurveyInviteItemDto {
  id: string;
  templateTitle: string;
  position: number;
  status: SurveyInviteItemStatus;
  questionCount: number;
  submittedAt: string | null;
}

export interface PublicSurveyInviteMeDto {
  tenantName: string;
  /** Nome e cognome del consulente; vuoto se l'account non ha un nome reale (mai l'email). */
  consultantName: string;
  /** Azienda del partner per cui lavora il consulente; null se l'invito non risale a un partner. */
  consultantCompanyName: string | null;
  message: string | null;
  expiresAt: string;
  items: PublicSurveyInviteItemDto[];
}

export interface PublicSurveyInviteSessionResponseDto {
  sessionToken: string;
  expiresAt: string;
  invite: PublicSurveyInviteMeDto;
}

export interface PublicSurveyInviteItemDetailDto {
  id: string;
  status: SurveyInviteItemStatus;
  schema: SurveySchema;
  draft: { answers: SurveyAnswers; currentNodeId: string } | null;
  hasSignature: boolean;
}

export interface PublicSurveyInviteItemStatusDto {
  status: SurveyInvitePdfStatus;
}

export interface PublicSurveyInviteClosedDto {
  reason: 'EXPIRED' | 'REVOKED' | 'NOT_FOUND';
}

export class UpsertPublicSurveyInviteDraftDto {
  @IsObject()
  answers!: SurveyAnswers;

  @IsString()
  @IsNotEmpty()
  @MaxLength(SURVEY_INVITE_NODE_ID_MAX_LENGTH)
  currentNodeId!: string;
}

export class SubmitPublicSurveyInviteDto {
  @IsObject()
  answers!: SurveyAnswers;

  @Transform(trimString)
  @IsString()
  @IsNotEmpty()
  @MaxLength(SURVEY_RESPONDENT_NAME_MAX_LENGTH)
  respondentName!: string;

  @IsIn(SURVEY_RESPONDENT_ROLES)
  respondentRole!: SurveyRespondentRole;

  /** Obbligatorio solo con ruolo `OTHER`; con gli altri ruoli viene ignorato (il service non lo persiste). */
  @ValidateIf((dto: SubmitPublicSurveyInviteDto) => dto.respondentRole === 'OTHER')
  @Transform(trimString)
  @IsString()
  @IsNotEmpty()
  @MaxLength(SURVEY_RESPONDENT_ROLE_OTHER_MAX_LENGTH)
  respondentRoleOther?: string;
}
